import ShapeBase from './ShapeBase';
import tools from '../tools';
import React, { useState } from 'react';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type UndoEntry from '../collaboration/UndoEntry';
import type Shape from '../objects/Shape';
import { normaliseDimensions } from './ShapeImage';

const ALLOWED_VIDEO_TYPES = [
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
] as const;
const VIDEO_PLACEHOLDER = `<svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512"><path fill="#dddddd" d="M0 96C0 60.7 28.7 32 64 32H448c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96zM48 368v32c0 8.8 7.2 16 16 16H96c8.8 0 16-7.2 16-16V368c0-8.8-7.2-16-16-16H64c-8.8 0-16 7.2-16 16zm368-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V368c0-8.8-7.2-16-16-16H416zM48 240v32c0 8.8 7.2 16 16 16H96c8.8 0 16-7.2 16-16V240c0-8.8-7.2-16-16-16H64c-8.8 0-16 7.2-16 16zm368-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V240c0-8.8-7.2-16-16-16H416zM48 112v32c0 8.8 7.2 16 16 16H96c8.8 0 16-7.2 16-16V112c0-8.8-7.2-16-16-16H64c-8.8 0-16 7.2-16 16zM416 96c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V112c0-8.8-7.2-16-16-16H416zM160 128v64c0 17.7 14.3 32 32 32H320c17.7 0 32-14.3 32-32V128c0-17.7-14.3-32-32-32H192c-17.7 0-32 14.3-32 32zm32 160c-17.7 0-32 14.3-32 32v64c0 17.7 14.3 32 32 32H320c17.7 0 32-14.3 32-32V320c0-17.7-14.3-32-32-32H192z"/></svg>`;

// --theme-tertiary-background
const DEFAULT_BG = '#F1F1F1';

const VideoContent = ({ shape }: { shape: ShapeVideo }) => {
  const [loaded, setLoaded] = useState(false);

  // We don't know the full dimensions until the media loads.
  const mediaLoaded = (width: number, height: number) => {
    const setDimensions = () => {
      normaliseDimensions(shape.fabricObject, width, height);
      shape.fabricObject.setCoords();
      shape.fabricObject.synchronize();
      shape.fabricObject.canvas.requestRenderAll();
      shape.fabricObject.canvas.externalContent.render(shape.fabricObject);
    };
    if (shape.creationUndoEntry) {
      // Since the load is asynchronous we need to augment the original undo entry
      // so that height/width is undone properly.
      shape.fabricObject.controller.undoManager.addUndo(
        shape.creationUndoEntry,
        setDimensions
      );
      shape.creationUndoEntry = null;
    } else {
      setDimensions();
    }

    setLoaded(true);
  };

  const style: React.CSSProperties = {
    position: 'absolute',
    width: '100%',
    height: '100%',
  };

  if (shape.fabricObject.fill && loaded) {
    style.backgroundColor = shape.fabricObject.fill;
  }

  if (shape.isVideo()) {
    return (
      <>
        {!loaded && <img src={ShapeVideo.encodedPlaceholder()} style={style} />}
        <video
          controls={shape.showControls}
          src={shape.src!}
          onLoadedMetadata={event => {
            const { videoWidth, videoHeight } = event.currentTarget;
            mediaLoaded(videoWidth, videoHeight);
          }}
          style={{ ...style, position: 'static', objectFit: 'fill' }}
        />
      </>
    );
  } else {
    return (
      <>
        {!loaded && <img src={ShapeVideo.encodedPlaceholder()} style={style} />}
        {shape.src && (
          <img
            src={shape.src}
            onLoad={event => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              mediaLoaded(naturalWidth, naturalHeight);
            }}
            style={style}
          />
        )}
      </>
    );
  }
};

class ShapeVideo extends ShapeBase {
  // URL of the attachment.
  src: string | null;

  // Whether to show video controls.
  showControls = false;

  creationUndoEntry: UndoEntry | null = null;

  // Image that will be used as placeholder when exporting.
  static placeholderImage: HTMLImageElement | null = null;

  constructor(fabricObject: Shape) {
    super(fabricObject);

    this.fabricObject.uniformScaling = true;
    this.fabricObject.allowLinearEdgeScaling = false;
    this.fabricObject.strokeWidth = 0;
    this.fabricObject.opacity ||= 1;
    this.fabricObject.stroke = undefined;
    this.fabricObject.fill ||= DEFAULT_BG;
    this.fabricObject.width ||= 100;
    this.fabricObject.height ||= 100;

    this.src = null;

    if (fabricObject.attachmentId) {
      void this.prepareImage();
    }

    if (this.isVideo()) {
      this.fabricObject.on('mousedblclick', this.showVideoControls);

      ['deselected', 'moving', 'rotating', 'scaling'].forEach(event =>
        this.fabricObject.on(event, this.hideVideoControls)
      );
    }

    ShapeVideo._preparePlaceholder();
  }

  static _preparePlaceholder() {
    if (ShapeVideo.placeholderImage) return;
    ShapeVideo.placeholderImage = new Image();
    ShapeVideo.placeholderImage.src = ShapeVideo.encodedPlaceholder();
    void ShapeVideo.placeholderImage.decode();
  }

  static encodedPlaceholder() {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      VIDEO_PLACEHOLDER
    )}`;
  }

  showVideoControls = () => {
    this.showControls = true;
    this.fabricObject.canvas.externalContent.render(this.fabricObject);
    this.fabricObject.canvas.externalContent.focus(this.fabricObject);
  };

  hideVideoControls = () => {
    this.showControls = false;
    this.fabricObject.canvas.externalContent.render(this.fabricObject);
    this.fabricObject.canvas.externalContent.blur();
  };

  static iconClass() {
    return 'fa-regular fa-film';
  }

  tools(topLevel = true) {
    return topLevel
      ? [
          ...(this.contentType === 'image/gif'
            ? [[tools.ToolFillColorWithoutOpacity]]
            : []),
          [tools.ToolDownloadImage],
        ]
      : [];
  }

  hasFixedDimensions() {
    return false;
  }

  hasEmbeddedText() {
    return false;
  }

  wasSet(keyOrPath: KeyPath, value: unknown) {
    super.wasSet(keyOrPath, value);

    if (keyOrPath === 'attachmentId') {
      void this.prepareImage();
    }
  }

  async prepareImage() {
    if (!this.attachmentId) return;

    // Resolve the attachment ID to a URL.
    const controller = this.fabricObject.controller;

    if (controller.props.resolveAttachmentInfo) {
      this.src = (
        await controller.props.resolveAttachmentInfo(this.attachmentId)
      ).url;
    } else {
      this.src = `/attachments/${this.attachmentId}`;
    }

    // TODO: force rerender of React.
    if (this.fabricObject.canvas) {
      this.fabricObject.canvas.externalContent.render(this.fabricObject);
    }
  }

  loadImage() {
    this.creationUndoEntry =
      this.fabricObject.controller.undoManager.currentUndoEntry();
  }

  isVideo() {
    if (this.contentType) {
      return ['video/mp4', 'video/webm'].includes(this.contentType);
    } else {
      return false;
    }
  }

  _renderPlaceholder(ctx: CanvasRenderingContext2D, obj: ShapeVideo) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#cccccc';
    ctx.strokeRect(-obj.width / 2, -obj.height / 2, obj.width, obj.height);
    ctx.fillStyle = '#f9f9f9';
    ctx.fillRect(-obj.width / 2, -obj.height / 2, obj.width, obj.height);

    // Size the placeholder image. Assumes square placeholder.
    const controllingDimension = Math.min(obj.width, obj.height) * 0.6;
    ctx.drawImage(
      ShapeVideo.placeholderImage!,
      -controllingDimension / 2,
      -controllingDimension / 2,
      controllingDimension,
      controllingDimension
    );
  }

  render(ctx: CanvasRenderingContext2D) {
    if (this.canvas.exporting && !this.canvas.isHTMLExport()) {
      this._renderPlaceholder(ctx, this);
    }
  }

  hasExternalContent() {
    return true;
  }

  clipExternalContent(): boolean {
    return !this.canvas.exporting || this.canvas.isHTMLExport();
  }

  renderExternalContent() {
    return <VideoContent shape={this} />;
  }
}

export default ShapeVideo;
export { ALLOWED_VIDEO_TYPES };
